import { FC, useEffect, useRef, useState } from 'react';
import { css, keyframes, Theme, useTheme } from '@emotion/react';
import { CheckCircle, Circle, CircleNotch, XCircle, MoonStars, Warning } from '@phosphor-icons/react';
import { StatusItemState } from 'common/status/StatusDocument';
import { Easing, spinAnimation } from 'client/Theme';

export enum State {
  OK = 1,
  WARNING = 2,
  ERROR = 3,
  LOADING = 4,
  DONE = 5,
  DISABLED = 0,
}

const stateToIcon: Record<StatusItemState, JSX.Element> = {
  [StatusItemState.GREEN]: <Circle weight="fill" />,
  [StatusItemState.YELLOW]: <Warning weight="fill" />,
  [StatusItemState.RED]: <XCircle weight="fill" />,
  [StatusItemState.GRAY]: <Circle weight="bold" />,
};

const pulseAnimation = keyframes`
  0% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const statusStyle = css`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const animatedStatusStyle = css`
  animation: ${pulseAnimation} 1s infinite alternate ${Easing.springy};
`;

const burstAnimation = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(2);
    opacity: 0;
  }
`;

const errorStyle = (theme: Theme) => css`
  &::after {
    content: '';
    position: absolute;
    display: block;
    top: 50%;
    left: 50%;
    width: 22px;
    height: 22px;
    background-color: ${theme.color.red[100]};
    background: radial-gradient(transparent 10%, ${theme.color.red[100]} 100%);
    border-radius: 50%;
    transform: translate(-50%, -50%);

    animation: ${burstAnimation} 2s infinite ${Easing.springy};
    animation-delay: inherit;
  }
`;

const loadingStyle = css`
  animation: ${spinAnimation} 1s infinite linear;
`;

const disabledStyle = css`
  animation: none;

  > svg > circle {
    stroke-width: 32px;
  }
`;

export interface StatusProps {
  itemStateID: StatusItemState | null;
  isPending?: boolean | null;
  isComplete?: boolean | null;
  isPaused?: boolean | null;
  size?: number;
  noBlink?: boolean;
}

// Render status page icon, supports blink, loading etc.
export const Status: FC<StatusProps> = ({
  itemStateID,
  isPending,
  isComplete = false,
  isPaused = false,
  size = 16,
  noBlink = false,
  ...other
}) => {
  const theme = useTheme();
  const statusRef = useRef<HTMLSpanElement>(null);
  const [position, setPosition] = useState(0);

  let icon = isPending ? <CircleNotch weight="bold" /> : stateToIcon[itemStateID || StatusItemState.GRAY];
  if (isComplete && itemStateID === StatusItemState.GREEN) icon = <CheckCircle weight="fill" />;
  if (isPaused) icon = <MoonStars weight="fill" />;

  const stateToColor: Record<StatusItemState, string> = {
    [StatusItemState.GREEN]: theme.color.green[100],
    [StatusItemState.YELLOW]: theme.color.yellow[100],
    [StatusItemState.RED]: theme.color.red[100],
    [StatusItemState.GRAY]: theme.color.grey[80],
  };

  let color = stateToColor[itemStateID || StatusItemState.GRAY];
  if (isPaused) color = theme.color.primary[100];

  const animationDelay = isPending ? 0 : position * 1.5;

  // Get this components position on page, used for cascading pulse effect
  useEffect(() => {
    if (!statusRef.current) return;
    setPosition(statusRef.current.getBoundingClientRect().y);
  }, [statusRef.current]);

  if (!itemStateID) {
    console.log('Status: NO STATE');
    return null;
  }

  return (
    <span
      ref={statusRef}
      css={[
        statusStyle,
        !(noBlink || isComplete) && animatedStatusStyle,
        !noBlink && itemStateID == StatusItemState.RED && errorStyle,
        itemStateID == StatusItemState.GRAY && disabledStyle,
        isPending && loadingStyle,
      ]}
      style={{ animationDelay: `${animationDelay}ms` }}
      {...other}
    >
      <icon.type {...icon.props} size={size} color={color} />
    </span>
  );
};
